import React, { useMemo, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { useMutation, useQuery } from '@tanstack/react-query';
import { toast } from 'sonner';
import PageHeader from '../shared/PageHeader';
import Button from '../../components/common/Button';
import { budgetsApi, departmentsApi } from '../../api/facades';

const BudgetCreatePage: React.FC = () => {
  const navigate = useNavigate();
  const currentYear = new Date().getFullYear();

  const [year, setYear] = useState<number>(currentYear);
  const [ceiling, setCeiling] = useState<string>('');
  const [departmentId, setDepartmentId] = useState<number | ''>('');
  const [category, setCategory] = useState('');
  const [description, setDescription] = useState('');
  const [isActive, setIsActive] = useState<'Yes' | 'No'>('Yes');

  const { data: departmentsRes } = useQuery({
    queryKey: ['departments'],
    queryFn: () => departmentsApi.list(),
  });

  const departments = useMemo(() => {
    try {
      const body = (departmentsRes as any)?.data;
      const inner = body?.data;
      if (Array.isArray(inner)) return inner;
      if (inner && typeof inner === 'object' && Array.isArray(inner.data)) return inner.data;
    } catch {
      // ignore
    }
    return [];
  }, [departmentsRes]);

  const yearOptions = Array.from({ length: 7 }).map((_, i) => currentYear - 1 + i);

  const createMu = useMutation({
    mutationFn: () =>
      budgetsApi.create({
        year,
        ceiling_amount: Number(ceiling || 0),
        department_id: typeof departmentId === 'number' ? departmentId : null,
        category: category || null,
        description: description || null,
        is_active: isActive,
      }),
    onSuccess: (res: any) => {
      toast.success('Budget created.');
      const id = res?.data?.data?.id ?? res?.data?.data?.budget?.id;
      if (id) {
        navigate(`/dashboard/budget/${id}`);
      } else {
        navigate('/dashboard/budget');
      }
    },
    onError: (e: any) => {
      const msg =
        e?.response?.data?.message ||
        e?.response?.data?.errors?.year?.[0] ||
        e?.response?.data?.errors?.ceiling_amount?.[0] ||
        'Failed to create budget.';
      toast.error(msg);
    },
  });

  return (
    <div className="space-y-4">
      <PageHeader
        hideTitle
        subtitle="Create an annual budget ceiling for the legal department or all departments."
      />

      <div className="bg-white dark:bg-slate-800/50 rounded-2xl p-6 border border-gray-100 dark:border-gray-700 max-w-2xl">
        <form
          onSubmit={(e) => {
            e.preventDefault();
            createMu.mutate();
          }}
          className="space-y-4"
        >
          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            <div>
              <label className="block text-sm font-medium mb-1 text-slate-700 dark:text-slate-200">
                Year
              </label>
              <select
                value={year}
                onChange={(e) => setYear(Number(e.target.value))}
                className="w-full rounded-lg border border-gray-300 bg-white dark:bg-slate-800 dark:border-gray-600 py-2 px-3 text-sm"
              >
                {yearOptions.map((y) => (
                  <option key={y} value={y}>
                    {y}
                  </option>
                ))}
              </select>
            </div>

            <div>
              <label className="block text-sm font-medium mb-1 text-slate-700 dark:text-slate-200">
                Ceiling amount (MWK)
              </label>
              <input
                type="number"
                min="0"
                step="0.01"
                value={ceiling}
                onChange={(e) => setCeiling(e.target.value)}
                required
                className="w-full rounded-lg border border-gray-300 bg-white dark:bg-slate-800 dark:border-gray-600 py-2 px-3 text-sm"
              />
            </div>
          </div>

          <div>
            <label className="block text-sm font-medium mb-1 text-slate-700 dark:text-slate-200">
              Department
            </label>
            <select
              value={departmentId === '' ? '' : departmentId}
              onChange={(e) => setDepartmentId(e.target.value === '' ? '' : Number(e.target.value))}
              className="w-full rounded-lg border border-gray-300 bg-white dark:bg-slate-800 dark:border-gray-600 py-2 px-3 text-sm"
            >
              <option value="">All departments (corporate budget)</option>
              {departments.map((d: any) => (
                <option key={d.id} value={d.id}>
                  {d.department_name}
                </option>
              ))}
            </select>
            <p className="mt-1 text-xs text-slate-500 dark:text-slate-400">
              Leave as &quot;All departments&quot; for a firm-wide legal budget, or pick a specific department like Legal.
            </p>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            <div>
              <label className="block text-sm font-medium mb-1 text-slate-700 dark:text-slate-200">
                Category (optional)
              </label>
              <input
                type="text"
                value={category}
                onChange={(e) => setCategory(e.target.value)}
                placeholder="e.g. Legal Fees, Court Costs"
                className="w-full rounded-lg border border-gray-300 bg-white dark:bg-slate-800 dark:border-gray-600 py-2 px-3 text-sm"
              />
            </div>
            <div className="flex items-center gap-2 mt-6 md:mt-7">
              <input
                id="budget-is-active"
                type="checkbox"
                checked={isActive === 'Yes'}
                onChange={(e) => setIsActive(e.target.checked ? 'Yes' : 'No')}
                className="h-4 w-4 rounded border-gray-300 text-primary-600 focus:ring-primary-500"
              />
              <label htmlFor="budget-is-active" className="text-sm text-slate-700 dark:text-slate-200">
                Active
              </label>
            </div>
          </div>

          <div>
            <label className="block text-sm font-medium mb-1 text-slate-700 dark:text-slate-200">
              Description (optional)
            </label>
            <textarea
              value={description}
              onChange={(e) => setDescription(e.target.value)}
              rows={3}
              className="w-full rounded-lg border border-gray-300 bg-white dark:bg-slate-800 dark:border-gray-600 py-2 px-3 text-sm"
            />
          </div>

          <div className="flex items-center justify-between pt-2">
            <Button variant="secondary" onClick={() => navigate('/dashboard/budget')}>
              Cancel
            </Button>
            <Button
              variant="primary"
              type="submit"
              disabled={createMu.isPending}
            >
              {createMu.isPending ? 'Saving…' : 'Save budget'}
            </Button>
          </div>
        </form>
      </div>
    </div>
  );
};

export default BudgetCreatePage;

